// ─────────────────────────────────────────────────────────────────────────────
// RexyGraphOverlay — REDUCED to two dock panels.
//
// It used to own a search-and-breadcrumb bar stretched across the middle of the
// scene and an inspector pinned to the top-right corner. Both are gone: the bar
// because the graph must load as a particle field with no text over it, and the
// corner inspector because it competed with the object it described.
//
// What remains are two panel BODIES with no positioning of their own. The dock
// decides where they live; they decide what they say. Nothing in this file
// renders over the canvas any more.
//
// The inspector shows what the SOURCE handed over and nothing more. It never
// reaches for a file, never enumerates a directory, and never displays a field
// the source did not supply — an access-filtered record in means an
// access-filtered panel out.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { RexyGraphNodeRecord } from './graphTypes';
import { RexyGraphStore } from './graphStore';
import { resolveDomainColour } from './graphPalette';
import { dockPanelTitleStyle } from './graphDock';
import { structuralTypeLabel } from './RexyGraphStructureTree';

// ── SEARCH ───────────────────────────────────────────────────────────────────

export interface GraphSearchPanelProps {
  store: RexyGraphStore;
  /** Live structure has zero record nodes, so the generic wording is retired there. */
  live?: boolean;
  query: string;
  results: RexyGraphNodeRecord[];
  breadcrumbs: string[];
  onQuery: (q: string) => void;
  onFocusChange: (focused: boolean) => void;
  onSelect: (id: string) => void;
  onFocusNode: (id: string) => void;
  onBack: () => void;
}

export function GraphSearchPanel({
  store, live, query, results, breadcrumbs, onQuery, onFocusChange, onSelect, onFocusNode, onBack,
}: GraphSearchPanelProps): React.ReactElement {
  return (
    <div>
      <div style={dockPanelTitleStyle}>search</div>
      <input
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        placeholder={live ? "domains, agents, memory, workflows…" : "nodes, agents, records…"}
        style={inputStyle}
        spellCheck={false}
      />

      {/* Breadcrumbs live HERE now — a compact row in the panel, never a strip
          of text laid over the graph. */}
      {breadcrumbs.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={dockPanelTitleStyle}>trail</div>
          <div style={crumbWrapStyle}>
            {breadcrumbs.length > 1 && (
              <button type="button" style={{ ...crumbStyle, color: '#4e6076' }} onClick={onBack}>
                ← back
              </button>
            )}
            {breadcrumbs.map((id, i) => {
              const node = store.getNode(id);
              if (!node) return null;
              return (
                <span key={id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {i > 0 && <span style={{ color: '#3d4c5e', margin: '0 5px' }}>›</span>}
                  <button type="button" style={crumbStyle} onClick={() => { onSelect(id); onFocusNode(id); }}>
                    {node.label}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={dockPanelTitleStyle}>{results.length} result{results.length === 1 ? '' : 's'}</div>
          {/* GROUPED BY REAL STRUCTURAL TYPE, never one undifferentiated list. A
              flat list forces the owner to guess whether a hit is a domain, an
              agent or a workflow — which is the same confusion this whole pass
              exists to remove. Each row states its owner, because a workflow named
              "Dreamstate" means nothing until you know whose it is. */}
          {groupResults(results, store, !!live).map(({ group, items }) => (
            <div key={group} style={{ marginBottom: 8 }}>
              <div style={{
                color: '#4e6076', fontSize: 9.5, letterSpacing: 0.7,
                textTransform: 'uppercase', marginBottom: 2,
              }}>{group}</div>
              {items.map((r) => {
                const owner = r.parentId ? store.getNode(r.parentId) : undefined;
                return (
                  <div key={r.id} style={resultRowStyle}>
                    <button type="button" style={resultLabelStyle} onClick={() => onSelect(r.id)}
                      title={r.label + ' — ' + structuralTypeLabel(r) + (owner ? ' · ' + owner.label : '')}>
                      <span style={{ color: resolveDomainColour(r.domainId || r.id, r.colour).hex }}>{r.label}</span>
                      <span style={{ color: '#4e6076', marginLeft: 6 }}>
                        {live ? structuralTypeLabel(r) : r.kind}{owner ? ' · ' + owner.label : ''}
                      </span>
                    </button>
                    <button type="button" style={miniActionStyle}
                      onClick={() => { onSelect(r.id); onFocusNode(r.id); }}>
                      focus
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {query && results.length === 0 && (
        <div style={{ marginTop: 14, color: '#4e6076' }}>no match</div>
      )}
    </div>
  );
}

// ── INSPECTOR ────────────────────────────────────────────────────────────────

export interface GraphInspectorPanelProps {
  store: RexyGraphStore;
  node: RexyGraphNodeRecord | null;
  onSelect: (id: string | null) => void;
  onFocusNode: (id: string) => void;
  /** Present only when the host can act on an openTarget. Absent → no OPEN button. */
  onOpen?: (node: RexyGraphNodeRecord) => void;
}

export function GraphInspectorPanel({
  store, node, onSelect, onFocusNode, onOpen,
}: GraphInspectorPanelProps): React.ReactElement {
  if (!node) {
    // Cleared selection clears the panel. Leaving the previous node's data on
    // screen would describe something that is no longer selected.
    return (
      <div>
        <div style={dockPanelTitleStyle}>inspector</div>
        <div style={{ color: '#4e6076' }}>No node selected.</div>
      </div>
    );
  }

  const parent = node.parentId ? store.getNode(node.parentId) : undefined;
  const children = store.childrenOf(node.id);
  const relations = store.edgesOf(node.id);
  const colour = resolveDomainColour(node.domainId || node.id, node.colour);
  const meta = (node.metadata || {}) as { [k: string]: unknown };
  const live = meta.live === true;
  const route = node.openTarget && node.openTarget.route ? node.openTarget.route : '';

  const related: RexyGraphNodeRecord[] = [];
  for (let i = 0; i < relations.length && related.length < 8; i++) {
    const edge = store.getEdge(relations[i]);
    if (!edge) continue;
    const otherId = edge.sourceId === node.id ? edge.targetId : edge.sourceId;
    const other = store.getNode(otherId);
    if (other) related.push(other);
  }

  return (
    <div>
      <div style={dockPanelTitleStyle}>inspector</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 9, height: 9, borderRadius: 5, background: colour.hex, flex: 'none' }} />
        <span style={{ color: '#e6eef8', fontSize: 12.5, wordBreak: 'break-word' }}>{node.label}</span>
      </div>

      <Field k="kind" v={node.kind} />
      <Field k="domain" v={node.domainId ? String(node.domainId).split(':').pop() || '—' : '—'} />
      <Field k="parent" v={parent ? parent.label : '—'} />
      {/* LIVE G2.0 CARRIES NO TRUST, NO MEMORY COUNT, NO MODEL AND NO TOOLS, and
          shows none of them: trust belongs to the memory integration that has not
          happened, and a tier rendered here would be an invented value. What a
          live node shows instead is what the OS actually states — its surface and
          the fact that no structural status field exists to read. */}
      {live ? (
        <>
          <Field k="route" v={route || 'NO DIRECT SURFACE'} mono={!!route} />
          <Field k="status" v={String(meta.structuralStatus || 'unknown')} />
          {/* G2.1 STRUCTURAL CHILD. Everything here is structural metadata the OS
              genuinely states — an owner, a layer, an isolation posture. No memory
              count, no record, no prompt, no model configuration, no credential.
              `owner` is spelled out because a subsystem's meaning depends on whose
              it is: the same store read as "the domain's" or "this agent's" is a
              different fact. */}
          {meta.structural ? (
            <>
              <Field k="owner" v={parent ? parent.label : String(node.ownerId || '—')} />
              <Field k="owner type" v={String(meta.ownerType || '—')} />
              <Field k="layer" v={structuralLayerLabel(String(meta.subKind || ''))} />
              {meta.partitionOf ? (
                <Field k="lives inside"
                       v={(store.getNode(String(meta.partitionOf))?.label) || String(meta.partitionOf)} />
              ) : null}
              {meta.isolation ? (
                <Field k="read isolation"
                       v={meta.isolation === 'full' ? 'both retrieval paths'
                          : meta.isolation === 'none' ? 'NONE — neither retrieval path'
                          : 'partial — ' + (meta.isolationPaths as string[] || []).join(', ')} />
              ) : null}
              {typeof meta.vectorShared === 'boolean' && meta.vectorShared
                ? <Field k="vector layer" v="shared by construction (domain opts out)" /> : null}
              {meta.tier ? <Field k="tier" v={String(meta.tier)} /> : null}
              {meta.engine ? <Field k="engine" v={String(meta.engine)} /> : null}
              {meta.provider ? <Field k="provider" v={String(meta.provider)} /> : null}
              <Field k="source" v="live OS structure" />
            </>
          ) : null}
          {meta.crossDomain ? <Field k="cross-domain" v="callable from any domain" /> : null}
          {meta.synthesisRead ? <Field k="synthesis read" v="reads the designed cross-domain set" /> : null}
          {node.kind === 'domain' ? <Field k="registered agents" v={String(node.childCount ?? 0)} /> : null}
          {Array.isArray(meta.tabs) && meta.tabs.length
            ? <Field k="tabs" v={(meta.tabs as string[]).join(' · ')} /> : null}
        </>
      ) : (
        <>
          <Field k="trust" v={node.trustTier || '—'} />
          <Field k="children" v={String(children.length || node.childCount || 0)} />
        </>
      )}
      <Field k="relations" v={String(relations.length)} />
      <Field k="source ref" v={node.id} mono />
      {!colour.resolved && (
        <div style={warnStyle}>colour unresolved — placeholder pending an owner ruling</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
        {/* OPEN appears only when the node carries a real route AND the host can
            act on it. A button that navigates nowhere is worse than no button. */}
        {onOpen && route && <Action label="open" onClick={() => onOpen(node)} />}
        <Action label="focus" onClick={() => onFocusNode(node.id)} />
        {parent && <Action label="parent" onClick={() => { onSelect(parent.id); onFocusNode(parent.id); }} />}
        {children.length > 0 && (
          <Action label="show children" onClick={() => { onSelect(children[0]); onFocusNode(children[0]); }} />
        )}
      </div>

      {/* The FULL child list, including anything past the 16/12 in-scene label
          cap. The cap is a DOM budget for the scene; it never limits what the
          owner can see here. */}
      {children.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={dockPanelTitleStyle}>children · {children.length}</div>
          <div style={{ maxHeight: 190, overflowY: 'auto' }}>
            {children.map((c) => {
              const child = store.getNode(c);
              if (!child) return null;
              return (
                <button key={c} type="button" style={listRowStyle} onClick={() => onSelect(c)}>
                  {child.label}
                  <span style={{ color: '#3d4c5e', marginLeft: 6 }}>{child.kind}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {related.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={dockPanelTitleStyle}>relations</div>
          {related.map((r) => (
            <button key={r.id} type="button" style={listRowStyle} onClick={() => onSelect(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      )}

      {/* OPEN stays absent: openTarget is a G2 field and no lab node carries one.
          A button that does nothing is worse than no button. */}
    </div>
  );
}

function Field({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={fieldRowStyle}>
      <span style={{ color: '#4e6076' }}>{k}</span>
      <span style={{ color: '#cfe6ff', fontSize: mono ? 9 : 10.5, textAlign: 'right', wordBreak: 'break-all' }}>
        {v}
      </span>
    </div>
  );
}

function Action({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" style={actionStyle} onClick={onClick}>{label}</button>;
}

const FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 9px',
  background: 'rgba(8,16,28,0.9)', border: '1px solid rgba(120,180,255,0.18)',
  borderRadius: 3, color: '#cfe6ff', fontFamily: FONT, fontSize: 11, outline: 'none',
};

const crumbWrapStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
};

const crumbStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', color: '#8fb4d8', cursor: 'pointer',
  fontFamily: FONT, fontSize: 10, letterSpacing: 0.6, padding: 0,
};

const resultRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  borderBottom: '1px solid rgba(120,180,255,0.07)', padding: '4px 0',
};

const resultLabelStyle: React.CSSProperties = {
  flex: 1, textAlign: 'left', background: 'transparent', border: 'none',
  color: '#cfe6ff', cursor: 'pointer', fontFamily: FONT, fontSize: 10.5, padding: 0,
};

const miniActionStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid rgba(120,190,255,0.28)', borderRadius: 2,
  color: '#8fb4d8', cursor: 'pointer', fontFamily: FONT, fontSize: 9, padding: '2px 5px',
};

const listRowStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '3px 0',
  background: 'transparent', border: 'none', color: '#8fb4d8', cursor: 'pointer',
  fontFamily: FONT, fontSize: 10,
};

const fieldRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 3,
};

const actionStyle: React.CSSProperties = {
  padding: '3px 7px', background: 'transparent',
  border: '1px solid rgba(120,190,255,0.32)', borderRadius: 2,
  color: '#cfe6ff', cursor: 'pointer', fontFamily: FONT, fontSize: 9.5, letterSpacing: 0.6,
};

const warnStyle: React.CSSProperties = {
  marginTop: 8, padding: '5px 7px', fontSize: 9.5, lineHeight: 1.4,
  color: '#FFD84A', background: 'rgba(255,216,74,0.07)',
  border: '1px solid rgba(255,216,74,0.22)', borderRadius: 2,
};

/**
 * The structural layer, in the owner's own words rather than the DTO's.
 *
 * "agent-space" is precise and unreadable; "this agent's memory, inside the
 * domain hive" says the same thing and carries the architecture with it — which
 * is the entire distinction G2.1 exists to make visible.
 */
function structuralLayerLabel(subKind: string): string {
  if (subKind === 'domain-hive') return 'domain memory (hive) — shared by the domain';
  if (subKind === 'agent-space') return "agent memory — this agent's namespace inside the hive";
  if (subKind === 'agent-store') return 'agent memory — its own separate store';
  if (subKind === 'os-master') return 'OS memory — belongs to no domain';
  if (subKind === 'vector-collection') return 'vector index';
  if (subKind === 'scheduled') return 'scheduled workflow';
  if (subKind === 'routing-fact') return 'model routing fact';
  return subKind || '—';
}


/** Search hits bucketed by structural type, in the tree's own order. */
function groupResults(results: RexyGraphNodeRecord[], store: RexyGraphStore, live: boolean) {
  if (!live) return [{ group: 'results', items: results }];
  const order = ['DOMAIN', 'AGENT', 'DOMAIN MEMORY', 'AGENT MEMORY', 'OS MEMORY',
                 'MEMORY SYSTEM', 'DATABASE', 'MODEL', 'WORKFLOW', 'SERVICE', 'SURFACE'];
  const buckets = new Map<string, RexyGraphNodeRecord[]>();
  results.forEach((r) => {
    const g = structuralTypeLabel(r);
    if (!buckets.has(g)) buckets.set(g, []);
    (buckets.get(g) as RexyGraphNodeRecord[]).push(r);
  });
  const out: { group: string; items: RexyGraphNodeRecord[] }[] = [];
  order.forEach((g) => {
    const items = buckets.get(g);
    if (items) { out.push({ group: g + 'S', items }); buckets.delete(g); }
  });
  buckets.forEach((items, g) => out.push({ group: g, items }));
  return out;
}
